/**
 * DataSourcesTab — Knowledge Base "what data does Trendy have access to" view.
 *
 * Renders the source tree returned by GET /api/knowledge/sources, with:
 *   - Counts + last-sync timestamps per leaf
 *   - Per-source hover actions: re-ingest, exclude toggle, view ingestion log
 *   - Collapsible groups (Epicor / M365 / Plaid / Training Corpus)
 *   - Inline ingestion-log drawer (slides down beneath the row)
 *
 * State is local — no global store needed. Refreshes on mount + on user demand.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Archive, Box, ChevronDown, ChevronRight, ClipboardList, Cloud, Database,
  EyeOff, Eye, FileText, Landmark, Loader2, Mail, PackageCheck, Receipt,
  RefreshCw, Ruler, Send, Table, Users, Wrench, AlertCircle, History,
} from 'lucide-react'
import * as api from '../../lib/api'

// Map server-supplied icon names to actual Lucide components. Adding a new
// icon name on the backend just needs a corresponding line here.
const ICONS = {
  Archive, Box, ClipboardList, Cloud, Database, FileText, Landmark, Mail,
  PackageCheck, Receipt, Ruler, Send, Table, Users, Wrench,
}

function iconFor(name) {
  return ICONS[name] || FileText
}

// Relative-time formatter — "4 min ago", "yesterday", "3 days ago".
function relTime(iso) {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  const diff = Math.max(0, Date.now() - then)
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m} min ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h} hr ago`
  const d = Math.floor(h / 24)
  if (d === 1) return 'yesterday'
  if (d < 30) return `${d} days ago`
  return new Date(iso).toLocaleDateString()
}

const COUNT_FMT = new Intl.NumberFormat('en-US')
function fmtCount(n) { return typeof n === 'number' ? COUNT_FMT.format(n) : '—' }

// ─── DataSourcesTab ────────────────────────────────────────────────────────

export default function DataSourcesTab() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  // Open log drawer keyed by source id
  const [openLog, setOpenLog] = useState(null)
  // Optimistic toggle state — track per-source-id while the POST is in flight
  const [pendingToggles, setPendingToggles] = useState(() => new Set())

  const load = useCallback(async ({ force = false } = {}) => {
    setError(null)
    if (force) setRefreshing(true)
    try {
      const res = await api.getKnowledgeSources({ refresh: force })
      setData(res)
    } catch (e) {
      setError(e?.message || 'Failed to load data sources')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleToggleExclusion = useCallback(async (sourceId, nextExcluded) => {
    setPendingToggles(prev => {
      const next = new Set(prev); next.add(sourceId); return next
    })
    // Optimistic UI: flip the excluded flag immediately
    setData(prev => prev && {
      ...prev,
      groups: prev.groups.map(g => ({
        ...g,
        items: g.items.map(it => it.id === sourceId ? { ...it, excluded: nextExcluded } : it),
      })),
    })
    try {
      await api.setSourceExclusion(sourceId, nextExcluded)
    } catch (e) {
      // Rollback
      setData(prev => prev && {
        ...prev,
        groups: prev.groups.map(g => ({
          ...g,
          items: g.items.map(it => it.id === sourceId ? { ...it, excluded: !nextExcluded } : it),
        })),
      })
      setError(e?.message || 'Failed to update exclusion')
    } finally {
      setPendingToggles(prev => {
        const next = new Set(prev); next.delete(sourceId); return next
      })
    }
  }, [])

  const handleReingest = useCallback(async (sourceId) => {
    try {
      const res = await api.reingestSource(sourceId)
      // Tiny toast via alert for PR3 — useToast wiring can come later
      // eslint-disable-next-line no-alert
      alert(res?.message || `Re-ingest queued for ${sourceId}`)
    } catch (e) {
      setError(e?.message || 'Failed to queue re-ingest')
    }
  }, [])

  const totalItems = useMemo(() => {
    if (!data?.groups) return 0
    return data.groups.reduce((acc, g) => acc + g.items.length, 0)
  }, [data])

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 size={16} className="animate-spin" />
        Loading data sources…
      </div>
    )
  }

  if (error) {
    return (
      <div className="px-4 py-3 rounded border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
        <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-medium">Couldn’t load the data source map.</div>
          <div className="text-xs mt-0.5">{error}</div>
          <button
            type="button"
            onClick={() => load({ force: true })}
            className="mt-2 text-xs text-amtrend-blue hover:underline"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header strip */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Data Trendy can access
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {totalItems} sources across {data?.groups?.length || 0} systems.
            {data?.cache_age_seconds ? (
              <> Counts refreshed {relTime(new Date(Date.now() - data.cache_age_seconds * 1000).toISOString())}.</>
            ) : null}
          </p>
        </div>
        <button
          type="button"
          onClick={() => load({ force: true })}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-surface-dark-overlay disabled:opacity-50"
        >
          <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Groups */}
      {data?.groups?.map(group => (
        <SourceGroup
          key={group.id}
          group={group}
          openLog={openLog}
          setOpenLog={setOpenLog}
          onToggleExclusion={handleToggleExclusion}
          onReingest={handleReingest}
          pendingToggles={pendingToggles}
        />
      ))}
    </div>
  )
}

// ─── SourceGroup ───────────────────────────────────────────────────────────

function SourceGroup({ group, openLog, setOpenLog, onToggleExclusion, onReingest, pendingToggles }) {
  const [collapsed, setCollapsed] = useState(false)
  const GroupIcon = iconFor(group.icon)
  return (
    <section className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-surface-dark-raised hover:bg-gray-100 dark:hover:bg-surface-dark-overlay"
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        <GroupIcon size={14} className="text-gray-500" />
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{group.label}</span>
        <span className="ml-auto text-[11px] text-gray-400 dark:text-gray-500">{group.items.length}</span>
      </button>
      {!collapsed && (
        <ul className="divide-y divide-gray-100 dark:divide-gray-900">
          {group.items.map(item => (
            <SourceItemRow
              key={item.id}
              item={item}
              isLogOpen={openLog === item.id}
              onToggleLog={() => setOpenLog(prev => prev === item.id ? null : item.id)}
              onToggleExclusion={onToggleExclusion}
              onReingest={onReingest}
              pendingToggle={pendingToggles.has(item.id)}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

// ─── SourceItemRow ─────────────────────────────────────────────────────────

function SourceItemRow({ item, isLogOpen, onToggleLog, onToggleExclusion, onReingest, pendingToggle }) {
  const Icon = iconFor(item.icon)
  const rel = relTime(item.last_sync)
  return (
    <li className={item.excluded ? 'bg-gray-50/50 dark:bg-surface-dark-raised/50 opacity-70' : ''}>
      <div className="group flex items-center gap-3 px-3 py-2">
        <Icon size={14} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm">
            <span className={'text-gray-800 dark:text-gray-100 truncate ' + (item.excluded ? 'line-through' : '')}>
              {item.label}
            </span>
            {item.excluded && (
              <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                Excluded
              </span>
            )}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
            {item.count != null ? `${fmtCount(item.count)} records` : (item.count_hint || 'count unavailable')}
            {rel ? <span> · synced {rel}</span> : null}
            {item.description ? <span> · {item.description}</span> : null}
          </div>
        </div>

        {/* Actions — visible on hover (sm+), always tappable on mobile */}
        <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          <IconButton
            label={isLogOpen ? 'Hide log' : 'View log'}
            onClick={onToggleLog}
            Icon={History}
          />
          <IconButton
            label="Re-ingest"
            onClick={() => onReingest(item.id)}
            Icon={RefreshCw}
            disabled={item.id.startsWith('plaid.not_configured') || item.count_hint === 'Not yet wired'}
          />
          <IconButton
            label={item.excluded ? 'Include' : 'Exclude'}
            onClick={() => onToggleExclusion(item.id, !item.excluded)}
            Icon={item.excluded ? Eye : EyeOff}
            disabled={pendingToggle}
            danger={!item.excluded}
          />
        </div>
      </div>
      {isLogOpen && <IngestLogDrawer sourceId={item.id} />}
    </li>
  )
}

function IconButton({ label, onClick, Icon, disabled, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={
        'p-1.5 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed ' +
        (danger
          ? 'text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30'
          : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-surface-dark-overlay')
      }
    >
      <Icon size={13} />
    </button>
  )
}

// ─── IngestLogDrawer ───────────────────────────────────────────────────────

function IngestLogDrawer({ sourceId }) {
  const [entries, setEntries] = useState(null)
  const [note, setNote] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    setEntries(null); setError(null); setNote(null)
    api.getIngestLog(sourceId)
      .then(r => {
        if (!alive) return
        setEntries(r.entries || [])
        setNote(r.note || null)
      })
      .catch(e => { if (alive) setError(e?.message || 'Failed to load log') })
    return () => { alive = false }
  }, [sourceId])

  if (error) {
    return <div className="px-3 pb-3 text-xs text-red-600 dark:text-red-400">{error}</div>
  }
  if (entries === null) {
    return (
      <div className="px-3 pb-3 text-xs text-gray-400 inline-flex items-center gap-1">
        <Loader2 size={11} className="animate-spin" /> Loading log…
      </div>
    )
  }
  if (entries.length === 0) {
    return (
      <div className="px-3 pb-3 text-xs text-gray-400">
        No telemetry recorded yet for this source.
        {note ? <span className="block mt-1 italic">{note}</span> : null}
      </div>
    )
  }
  return (
    <div className="px-3 pb-3">
      <table className="w-full text-[11px] border-separate border-spacing-0">
        <thead>
          <tr className="text-gray-400 dark:text-gray-500 uppercase tracking-wide">
            <th className="text-left py-1 pr-3">When</th>
            <th className="text-left py-1 pr-3">User</th>
            <th className="text-right py-1 pr-3">Duration</th>
            <th className="text-left py-1">Status</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => (
            <tr key={i} className="border-t border-gray-100 dark:border-gray-900">
              <td className="py-1 pr-3 text-gray-700 dark:text-gray-300 tabular-nums">{relTime(e.at) || e.at}</td>
              <td className="py-1 pr-3 text-gray-500 dark:text-gray-400">{e.username || '—'}</td>
              <td className="py-1 pr-3 text-right text-gray-500 dark:text-gray-400 tabular-nums">
                {e.duration_ms != null ? `${Math.round(e.duration_ms)}ms` : '—'}
              </td>
              <td className="py-1">
                {e.success ? (
                  <span className="text-green-600 dark:text-green-400">OK</span>
                ) : (
                  <span className="text-red-600 dark:text-red-400">
                    {e.timeout ? 'Timeout' : 'Failed'}
                    {e.fallback_used ? ' · fallback' : ''}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
